/**
 * ZenToastConfig for the web build.
 *
 * react-native-toast-message keeps its toast mounted at all times and parks it
 * just below the viewport when hidden. In a browser that parked toast is still
 * in the tab order and the accessibility tree: Tab from the sign-in button
 * lands on an invisible "Success" toast and its close icon, and screen readers
 * announce "Success" on every page.
 *
 * Each renderer is wrapped so that, while the library reports the toast as not
 * visible, it is `visibility: hidden` - out of the tab order and the
 * accessibility tree. The toast's own look and content are unchanged.
 */

import React from 'react';
import {StyleSheet, View} from 'react-native';
import {ZenToastConfig as BaseToastConfig} from 'zen-core-elements/index';

type Renderer = (params: any) => React.ReactNode;

export const ZenToastConfig = Object.fromEntries(
  Object.entries(BaseToastConfig as Record<string, Renderer>).map(([type, render]) => [
    type,
    (params: any) => (
      <View style={[styles.frame, params?.isVisible === false && hidden]}>{render(params)}</View>
    ),
  ]),
) as typeof BaseToastConfig;

const styles = StyleSheet.create({
  /*
   * Must span the full width and centre its child exactly like the library's
   * container (left/right 0, alignItems center). The toast sizes itself as a
   * percentage of its parent ("38%" on desktop); a wrapper that shrank to its
   * content made that 38% of almost nothing - a collapsed toast with only the
   * icon and close button showing.
   */
  frame: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

// `visibility` is a web-only style key that react-native-web passes through.
const hidden = {visibility: 'hidden'} as any;
